<script setup>
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, ref } from "vue";
import moment from "moment";
import billingApi from "@/api/billing";
import DataTable from "@/components/panels/DataTable.vue";
import RowActions from "@/components/panels/RowActions.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

const transactionList = computed(() => store.getters['billing/transactionList']);
const organizationList = computed(() => store.getters['organization/organizationList']);
const transactions = computed(() => transactionList.value?.results || []);

const page = ref(1);
const type = ref('');
const organization = ref('');

// append-only ledger: rows are created by the billing engine, UI only reads
const TYPE_META = {
  charge_monthly: { cls: 'invoice', icon: 'file-document-outline', label: 'Monthly charge' },
  charge_per_use: { cls: 'invoice', icon: 'file-document-outline', label: 'Per-use charge' },
  top_up: { cls: 'payment', icon: 'cash-check', label: 'Top-up' },
  adjustment: { cls: 'writeoff', icon: 'file-cancel-outline', label: 'Adjustment' },
};

function getFilteredTransactionList() {
  const loader = $loading.show({});
  let filter = `?page=${page.value}`;
  if (type.value) filter += `&type=${type.value}`;
  if (organization.value) filter += `&organization=${organization.value}`;
  return store.dispatch('billing/getTransactionList', filter)
    .finally(() => loader.hide());
}

function applyFilters() {
  page.value = 1;
  getFilteredTransactionList();
}

// KPI: total balance — from the organization list; counters — count by filters
const typeCounts = ref({ charges: 0, top_up: 0, adjustment: 0, month: 0 });

function loadCounts() {
  const count = (filter) =>
    billingApi.getTransactionList(`?page_size=1${filter}`).then((r) => r.data.count ?? 0).catch(() => 0);
  Promise.all([
    count('&type=charge_monthly'),
    count('&type=charge_per_use'),
  ]).then(([m, u]) => { typeCounts.value.charges = m + u; });
  count('&type=top_up').then((n) => { typeCounts.value.top_up = n; });
  count('&type=adjustment').then((n) => { typeCounts.value.adjustment = n; });
  count(`&date_from=${moment().startOf('month').format('YYYY-MM-DD')}`)
    .then((n) => { typeCounts.value.month = n; });
}

onMounted(() => {
  getFilteredTransactionList();
  loadCounts();
  store.dispatch('organization/getOrganizationList', '?page_size=1000');
});

const totalBalance = computed(() => {
  const orgs = organizationList.value?.results || [];
  return orgs.reduce((s, o) => s + (parseFloat(o.balance) || 0), 0);
});

const money = (n) =>
  '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const metrics = computed(() => [
  { label: 'Charges', value: typeCounts.value.charges, icon: 'file-document-outline', color: 'var(--lb-blue)' },
  { label: 'Top-ups', value: typeCounts.value.top_up, icon: 'cash-check', color: 'var(--lb-success)' },
  { label: 'Adjustments', value: typeCounts.value.adjustment, icon: 'file-cancel-outline', color: 'var(--lb-red)' },
  { label: 'This Month', value: typeCounts.value.month, icon: 'calendar-month-outline', color: 'var(--lb-warning)' },
]);

// TODO(avg invoices amount / last week invoices): no backend endpoint for this
// statistic yet — demo data for now, swap for real data once the API exists.
const avgInvoicesAmount = { value: '$8.20', foot: 'Across all transactions' };
const weekDemo = [
  { day: 'Mon', v: 2 }, { day: 'Tue', v: 4 }, { day: 'Wed', v: 3 },
  { day: 'Thu', v: 5 }, { day: 'Fri', v: 3 }, { day: 'Sat', v: 1 }, { day: 'Sun', v: 1 },
];
const weekMax = Math.max(...weekDemo.map((d) => d.v));
const weekTotal = weekDemo.reduce((s, d) => s + d.v, 0);

const fmtDate = (d) => (d ? moment(d).format('MMM D, YYYY') : '—');
const shortId = (id) => String(id).split('-')[0].toUpperCase();

const cols = 'minmax(120px,0.85fr) minmax(150px,1fr) minmax(160px,0.95fr) 120px 120px minmax(160px,1fr)';
const columns = ['Transaction', 'Organization', 'Type', 'Date', 'Amount', 'Details'];
</script>

<template>
  <!-- metrics -->
  <div class="ro-top">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-cash-multiple"></span>
        <div class="mc-total__title">Total balances</div>
        <div class="mc-total__num mc-total__num--money">{{ money(totalBalance) }}</div>
      </div>
      <div v-for="m in metrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>

    <div class="ro-right">
      <div class="ro-card avgcard">
        <span class="avgcard__wm mdi mdi-receipt-text-outline"></span>
        <div class="avgcard__title">Avg. invoices amount</div>
        <div class="avgcard__num">{{ avgInvoicesAmount.value }}</div>
        <div class="avgcard__foot">{{ avgInvoicesAmount.foot }}</div>
      </div>
      <div class="ro-card">
        <div class="ro-card__head">
          <span class="mdi mdi-chart-bar"></span>
          <h3>Last Week invoices</h3>
          <span class="sub">{{ weekTotal }} total</span>
        </div>
        <div class="weekchart">
          <div v-for="d in weekDemo" :key="d.day" class="weekbar" :class="{ 'is-top': d.v === weekMax }">
            <div class="weekbar__val">{{ d.v }}</div>
            <div class="weekbar__track">
              <div class="weekbar__fill" :style="{ height: `${(d.v / weekMax) * 100}%` }"></div>
            </div>
            <div class="weekbar__day">{{ d.day }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- toolbar -->
  <div class="toolbar">
    <div class="field-select">
      <span class="mdi mdi-tag-outline lead"></span>
      <select v-model="type" @change="applyFilters">
        <option value="">All types</option>
        <option v-for="(meta, key) in TYPE_META" :key="key" :value="key">{{ meta.label }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="organizationList" class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="organization" @change="applyFilters">
        <option value="">All organizations</option>
        <option v-for="o in organizationList.results || []" :key="o.id" :value="o.id">{{ o.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ transactionList?.count ?? 0 }} transactions</span>
  </div>

  <!-- table -->
  <DataTable :cols="cols" :columns="columns" :rows="transactions" empty-text="No transactions match your filters.">
    <template #row="{ row }">
      <div class="td"><span class="mono strong">TXN-{{ shortId(row.id) }}</span></div>
      <div class="td"><span>{{ row.organization?.name || '—' }}</span></div>
      <div class="td">
        <span class="planchip" :class="`planchip--${(TYPE_META[row.type] || TYPE_META.adjustment).cls}`">
          <span class="mdi" :class="`mdi-${(TYPE_META[row.type] || TYPE_META.adjustment).icon}`"></span>
          {{ (TYPE_META[row.type] || { label: row.type }).label }}
        </span>
      </div>
      <div class="td"><span class="muted">{{ fmtDate(row.date || row.created_at) }}</span></div>
      <div class="td"><span class="strong">{{ money(row.amount) }}</span></div>
      <div class="td">
        <span class="muted">{{ row.extra ? Object.entries(row.extra).map(([k, v]) => `${k}: ${v}`).join(' · ') : '—' }}</span>
      </div>
      <RowActions :copies="[{ icon: 'content-copy', label: 'Copy transaction ID', text: row.id }]" />
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="transactionList?.total_pages || 1"
    :count="transactionList?.count || 0"
    @update:page="(p) => { page = p; getFilteredTransactionList(); }" />
</template>
